import { useId, useRef } from 'react';
import type { MouseEvent, ReactNode, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';
import { useLayer } from '../../lib/layers';

export type DialogSize = 'sm' | 'md' | 'lg';

const WIDTHS: Record<DialogSize, number> = { sm: 480, md: 520, lg: 560 };

export interface DialogProps {
  readonly title: ReactNode;
  /** Explanatory text under the title (fg-2, 14px / 1.5). */
  readonly description?: ReactNode;
  readonly onClose: () => void;
  /** 480 / 520 / 560 px, as used by the screens. */
  readonly size?: DialogSize;
  /** `alertdialog` for confirmations of consequential actions (issue, cancel, mark paid). */
  readonly role?: 'dialog' | 'alertdialog';
  /** Right-aligned buttons; Cancel (ghost) first, then the confirm button (ink). */
  readonly footer?: ReactNode;
  /** Closing on a scrim click; on by default (design). */
  readonly closeOnScrim?: boolean;
  readonly initialFocusRef?: RefObject<HTMLElement | null>;
  readonly className?: string;
  readonly children?: ReactNode;
}

/**
 * Centered modal (design: scrim rgba(15,20,25,0.36), card radius 16, shadow-lg, padding
 * 20px 24px, gap 12px, title 20px/600). Esc and a click on the scrim close it; Tab is trapped;
 * focus goes to the first control and back to the opener on close.
 */
export function Dialog({
  title,
  description,
  onClose,
  size = 'sm',
  role = 'dialog',
  footer,
  closeOnScrim = true,
  initialFocusRef,
  className,
  children,
}: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descriptionId = useId();
  useLayer({ kind: 'dialog', panelRef, onClose, initialFocusRef });

  const onScrimMouseDown = (event: MouseEvent<HTMLDivElement>) => {
    if (closeOnScrim && event.target === event.currentTarget) onClose();
  };

  return createPortal(
    <div
      data-testid="dialog-scrim"
      onMouseDown={onScrimMouseDown}
      className="fixed inset-0 z-50 grid place-items-center bg-scrim text-[13px] text-fg-1"
    >
      <div
        ref={panelRef}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description !== undefined ? descriptionId : undefined}
        tabIndex={-1}
        className={cn(
          'flex max-h-[80vh] max-w-[calc(100vw-48px)] flex-col gap-3 overflow-y-auto rounded-3 bg-bg-3 px-6 py-5 shadow-lg outline-none',
          className,
        )}
        style={{ width: WIDTHS[size] }}
      >
        <span id={titleId} className="text-[20px] font-semibold tracking-[-0.005em]">
          {title}
        </span>
        {description !== undefined && (
          <span id={descriptionId} className="text-[14px] leading-[1.5] text-fg-2 [text-wrap:pretty]">
            {description}
          </span>
        )}
        {children}
        {footer !== undefined && <div className="flex justify-end gap-2">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
